import React from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import InlineFeedback, { type InlineFeedbackProps } from '@/components/shared/InlineFeedback';

export type InvoiceConfirmVariant = 'delete' | 'archive' | 'unarchive';
export type InvoiceDialogFeedback = Pick<InlineFeedbackProps, 'variant' | 'title' | 'message'>;

const COPY: Record<InvoiceConfirmVariant, { title: string; description: string }> = {
  delete: {
    title: 'Delete invoice',
    description: 'Delete this invoice? This cannot be undone.',
  },
  archive: {
    title: 'Archive invoice',
    description: 'Archive this invoice?',
  },
  unarchive: {
    title: 'Unarchive invoice',
    description: 'Unarchive this invoice?',
  },
};

interface InvoiceConfirmDialogProps {
  open: boolean;
  variant: InvoiceConfirmVariant;
  isSubmitting?: boolean;
  feedback?: InvoiceDialogFeedback | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const InvoiceConfirmDialog: React.FC<InvoiceConfirmDialogProps> = ({
  open,
  variant,
  isSubmitting = false,
  feedback,
  onCancel,
  onConfirm,
}) => {
  const { title, description } = COPY[variant];
  const isDelete = variant === 'delete';

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSubmitting) onCancel();
      }}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 600, pb: 0.5 }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 0 }}>
        <Typography sx={{ fontSize: '0.9rem', color: '#4a5565' }}>{description}</Typography>
        {feedback ? (
          <Box sx={{ mt: 2 }}>
            <InlineFeedback
              variant={feedback.variant}
              title={feedback.title}
              message={feedback.message}
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, pt: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={isSubmitting}
          sx={{ textTransform: 'none', borderRadius: '10px' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSubmitting}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            ...(isDelete
              ? { bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }
              : { bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }),
          }}
        >
          {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceConfirmDialog;
